/**
 * Test track under a timeline: the thin reactive half (2026-10-13). The rules live in `testTrack.ts`; this file
 * only fetches from OPBridge, caches per track and polls the playhead while a track plays.
 *
 * Every read goes to the PRIMARY bridge (file sync puts every track on every PC, so any bridge can describe it);
 * play goes to the same bridge with `pc` naming the PC that should make the sound. A bridge without the routes
 * answers 404: `available()` is then false and the editor shows why instead of an empty row.
 */
import { createSignal } from 'solid-js';
import {
  bridges, getConnection, primaryBridge, primaryHttpClient,
} from '../opbridge/store';
import type {
  AudioKind, AudioOutput, TestTrack, TestTrackBeatmap, TestTrackBeatmapPatch, TestTrackPeaks, TestTrackSectionMark, TestTrackStatus,
  TestTrackTempoChoice,
} from '../opbridge/httpClient';
import { createHistory, type HistoryHandle } from '../undo/history';
import { pushToast } from '../ui/toast';
import { noteEdit, registerOrdered } from '../undo/editOrder';


const [tracks, setTracks] = createSignal<TestTrack[]>([]);
/** The list came back from OPBridge at least once (so a song NOT in it is really gone, not just not loaded yet). */
const [tracksLoaded, setTracksLoaded] = createSignal(false);
/** A timeline's song that is no longer in the audio library (2026-10-16: a deleted song left its timeline saying "not
 *  analysed yet", for ever; found on the operator's "Danger Zone v1"). */
export function songMissing(id: string | undefined): boolean {
  return !!id && tracksLoaded() && !tracks().some((t) => t.id === id);
}
const [unavailable, setUnavailable] = createSignal<string | null>(null);
const [beatmaps, setBeatmaps] = createSignal<Record<string, TestTrackBeatmap | null>>({});
const [peaksById, setPeaksById] = createSignal<Record<string, TestTrackPeaks | null>>({});
const [status, setStatus] = createSignal<TestTrackStatus | null>(null);
const [lastError, setLastError] = createSignal<string | null>(null);
/** A song being imported (dropped or picked): uploading, then OPBridge's helper analysing it, then done or failed. */
export interface Importing {
  name: string; state: 'waiting' | 'uploading' | 'analysing' | 'done' | 'failed'; message?: string; trackId?: string;
  /** The file was already in the library (OPBridge S327 `existing: true`): that track, nothing copied or analysed. */
  existing?: boolean;
  /** Which import this is (a later one takes over; an earlier one's late updates are dropped). */
  token?: number;
  /** The timeline the song will be linked to once analysed. */
  timelineName?: string;
  /** Upload progress in bytes (the browser's own count). */
  sent?: number; total?: number;
  /** When OPBridge's helper started analysing, for the time shown. */
  analysisStartMs?: number;
  /** Waiting for its turn to be analysed (OPBridge analyses one at a time). */
  queued?: boolean;
}
const [importing, setImporting] = createSignal<Importing | null>(null);
/** The import window (`components/SongImport.tsx`): opened by every import, closed or hidden by the operator. */
const [importModalOpen, setImportModalOpen] = createSignal(false);
export { importModalOpen, setImportModalOpen };

export { tracks, beatmaps, peaksById, status, lastError, importing };

// ---- undo for the song (2026-10-13, operator: "there's no undo"). Section marks, the tempo pick and bar 1 live in
// OPBridge's beat map, not in the timeline document, so the timeline's history could never reach them. This history
// snapshots those fields per loaded track; undo / redo write the snapshot back to OPBridge (and the cache at once).
interface SongFields { sections: TestTrackSectionMark[]; tempoChoice: TestTrackTempoChoice; bar1Sec: number }
const fieldsOf = (m: TestTrackBeatmap): SongFields => ({ sections: m.sections ?? [], tempoChoice: m.tempoChoice ?? 'as-read', bar1Sec: m.bar1_sec });
const sameFields = (a: SongFields, b: SongFields) => JSON.stringify(a) === JSON.stringify(b);
export const songHistory = createHistory<Record<string, SongFields>>({
  name: 'song',
  read: () => {
    const out: Record<string, SongFields> = {};
    for (const [id, m] of Object.entries(beatmaps())) if (m) out[id] = fieldsOf(m);
    return out;
  },
  write: (next) => {
    for (const [id, f] of Object.entries(next)) {
      const cur = beatmaps()[id];
      if (!cur || sameFields(fieldsOf(cur), f)) continue;
      setBeatmaps((o) => ({ ...o, [id]: { ...cur, sections: f.sections, tempoChoice: f.tempoChoice, bar1_sec: f.bar1Sec } }));
      void sendPatch(id, { sections: f.sections, tempoChoice: f.tempoChoice, bar1Sec: f.bar1Sec });
    }
  },
  onCheckpoint: () => noteEdit('song'),
});
registerOrdered('song', songHistory as HistoryHandle<unknown>);

/** Is this a file OPBridge can import? Audio by type, or by extension when the browser does not say. */
export function looksLikeAudio(f: File): boolean {
  return f.type.startsWith('audio/') || /\.(mp3|wav|flac|m4a|aac|aif|aiff|ogg|opus)$/i.test(f.name);
}

// ---- the import queue (2026-10-13, the audio library: "if there are multiple, it'd be a modal queue"). Every import
// goes through here: a drop on the timeline's song row, "Import a song...", the Audio section, a replace. One file
// at a time (the uploads share one link and OPBridge analyses one at a time on the GPU); the processing window
// (`components/SongImport.tsx`) shows the queue, a row per file; the song row's notice follows the file being worked on.

/** One file in the queue. */
export interface QueueItem extends Importing { key: number; kind?: AudioKind; replaceOf?: string }
export interface EnqueueOpts {
  /** The timeline the file will be linked to (the window names it). */
  timelineName?: string;
  /** The type the operator chose by where they dropped it (the Audio section's One-shots / Loops / Songs). */
  kind?: AudioKind;
  /** Replace this file's audio, keeping its id (and its marks unless `keep` is false). */
  replaceOf?: string; keep?: boolean;
}
const [audioQueue, setAudioQueue] = createSignal<QueueItem[]>([]);
export { audioQueue };
interface Job { file: File; opts: EnqueueOpts; resolve: (t: TestTrack | null) => void }
const jobs = new Map<number, Job>();
let queueKey = 0;
let running = false;
const updItem = (key: number, patch: Partial<QueueItem> | ((i: QueueItem) => QueueItem)) => {
  setAudioQueue((q) => q.map((it) => (it.key === key ? (typeof patch === 'function' ? patch(it) : { ...it, ...patch }) : it)));
  const it = audioQueue().find((x) => x.key === key);
  if (it) setImporting(it);
};

/** Does the primary OPBridge keep the audio library (type, name, replace)? Asked of OPBridge 2026-10-13. */
export function audioLibraryReady(): boolean {
  const p = primaryBridge();
  return !!p && getConnection(p.id)?.health()?.capabilities?.['audio_library'] === true;
}

/** Queue files for import (or one for a replace); resolves, per file, with the track once analysed or null. */
export function enqueueAudio(files: File[], opts: EnqueueOpts = {}): Promise<Array<TestTrack | null>> {
  setImportModalOpen(true);
  // A finished batch clears when the next one starts.
  if (!running) setAudioQueue((q) => q.filter((it) => it.state !== 'done' && it.state !== 'failed'));
  const all = files.map((file) => new Promise<TestTrack | null>((resolve) => {
    const key = ++queueKey;
    jobs.set(key, { file, opts, resolve });
    setAudioQueue((q) => [...q, {
      key, token: key, name: file.name.replace(/\.[^.]+$/, ''), state: 'waiting', total: file.size,
      ...(opts.timelineName ? { timelineName: opts.timelineName } : {}),
      ...(opts.kind ? { kind: opts.kind } : {}),
      ...(opts.replaceOf ? { replaceOf: opts.replaceOf } : {}),
    }]);
  }));
  void runQueue();
  return Promise.all(all);
}

/** Import one audio file (through the queue). */
export async function importTrack(file: File, opts: EnqueueOpts = {}): Promise<TestTrack | null> {
  return (await enqueueAudio([file], opts))[0] ?? null;
}

async function runQueue(): Promise<void> {
  if (running) return;
  running = true;
  try {
    for (;;) {
      const next = audioQueue().find((it) => it.state === 'waiting');
      if (!next) break;
      const job = jobs.get(next.key);
      jobs.delete(next.key);
      const t = job ? await runItem(next.key, job) : null;
      job?.resolve(t);
    }
  } finally { running = false; }
}

async function runItem(key: number, job: Job): Promise<TestTrack | null> {
  const { file, opts } = job;
  const name = file.name.replace(/\.[^.]+$/, '');
  const c = primaryHttpClient();
  if (!c) { updItem(key, { state: 'failed', message: 'No OPBridge is connected.' }); return null; }
  if (!looksLikeAudio(file)) { updItem(key, { state: 'failed', message: `${file.name} is not an audio file. OPBridge plays MP3, WAV, FLAC, AIFF and OGG (Vorbis).` }); return null; }
  updItem(key, { state: 'uploading', sent: 0, total: file.size });
  // Two minutes, plus a minute per 8 MB: a large WAV over an iPad's Wi-Fi takes longer than a small MP3.
  const timeoutMs = 120000 + Math.ceil(file.size / 8_388_608) * 60000;
  const onProgress = (sent: number, total: number) => updItem(key, (it) => (it.state === 'uploading' ? { ...it, sent, total } : it));
  let track: TestTrack;
  let existing = false;
  try {
    const r = opts.replaceOf
      ? await c.replaceTestTrack(opts.replaceOf, file, opts.keep !== false, timeoutMs, onProgress)
      : await c.importTestTrack(file, timeoutMs, onProgress);
    track = r.track;
    existing = !opts.replaceOf && (r as { existing?: boolean }).existing === true;
  } catch (e) {
    updItem(key, { state: 'failed', message: message(e) });
    return null;
  }
  // Already in the library (OPBridge S327: the same audio, byte for byte): that track is the answer, with its marks
  // and tempo pick as they are. Nothing to wait for, and its type is left as it was (dropping it on Loops does not
  // re-sort a song the operator already filed).
  if (existing) {
    await refreshTracks();
    updItem(key, { name: track.name || name, state: 'done', trackId: track.id, existing: true });
    return tracks().find((x) => x.id === track.id) ?? track;
  }
  updItem(key, { name: track.name || name, state: 'analysing', trackId: track.id, analysisStartMs: Date.now() });
  // The helper takes ~30-40 s for a song on the dev PC (OPBridge S318 measured 36 s). Poll the list, 1.5 s apart.
  const until = Date.now() + 5 * 60_000;
  let lost = 0;
  let gone = 0;
  for (;;) {
    await new Promise((r) => setTimeout(r, 1500));
    await refreshTracks();
    if (unavailable()) { lost++; if (lost >= 3) { updItem(key, { state: 'failed', message: `Lost OPBridge while it analysed: ${unavailable()}` }); return null; } continue; }
    lost = 0;
    const t = tracks().find((x) => x.id === track.id);
    // Deleted while it waited or analysed (the Audio section's Delete): say so and let the queue move on, instead of
    // waiting five minutes for a file that is gone (found 2026-10-13 by the audio_library smoke: a deleted file held
    // the next import, a replace, behind it).
    if (!t) { gone++; if (gone >= 2) { updItem(key, { state: 'failed', message: 'It was deleted before it finished.' }); return null; } continue; }
    gone = 0;
    updItem(key, { queued: !!t.queued });
    if (t?.analysisError) { updItem(key, { name: t.name, state: 'failed', message: t.analysisError }); return t; }
    if (t?.analysed && !t.analysing && !t.queued) {
      // A replace changed the audio: the cached beat map and waveform are the old file's.
      void loadTrack(t.id, true);
      // The type the operator chose by where they dropped it, kept by OPBridge when it can.
      if (opts.kind && audioLibraryReady()) { try { await c.patchTestTrack(t.id, { kind: opts.kind }); await refreshTracks(); } catch (e) { setLastError(message(e)); } }
      updItem(key, { name: t.name, state: 'done' });
      return tracks().find((x) => x.id === t.id) ?? t;
    }
    if (Date.now() > until) { updItem(key, { state: 'failed', message: 'The analysis is taking longer than 5 minutes.' }); return t ?? track; }
  }
}

/** Rename a file or set its type (OPBridge keeps both: `audio_library`). */
export async function patchAudio(id: string, patch: { name?: string; kind?: AudioKind }): Promise<boolean> {
  const c = primaryHttpClient();
  if (!c) return false;
  try { await c.patchTestTrack(id, patch); await refreshTracks(); return true; }
  catch (e) { const why = message(e); setLastError(why); pushToast('error', `OPBridge did not save it: ${why}`); return false; }
}

/** Delete a file and its beat map (every PC, by file sync). */
export async function deleteAudio(id: string): Promise<boolean> {
  const c = primaryHttpClient();
  if (!c) return false;
  try {
    await c.deleteTestTrack(id);
    setBeatmaps((o) => { const n = { ...o }; delete n[id]; return n; });
    setPeaksById((o) => { const n = { ...o }; delete n[id]; return n; });
    await refreshTracks();
    return true;
  } catch (e) { const why = message(e); setLastError(why); pushToast('error', `OPBridge did not delete it: ${why}`); return false; }
}

/** Why the track row cannot work, or null when it can. Reactive. */
export function unavailableReason(): string | null {
  const primary = primaryBridge();
  if (!primary) return 'No OPBridge is set up.';
  const conn = getConnection(primary.id);
  if (!conn || conn.statusSignal()() !== 'connected') return `${primary.name} is not connected.`;
  return unavailable();
}

/** OPBridge's own words for a refusal: its errors are a status plus `{"error": "<code>", "message": "..."}`
 *  (e.g. 409 `performance_mode`, `ableton_playing`, 503 `pc_unreachable`), carried at the end of our error text. */
const message = (e: unknown): string => {
  const text = e instanceof Error ? e.message : String(e);
  const at = text.indexOf('{');
  if (at >= 0) {
    try {
      const body = JSON.parse(text.slice(at)) as { error?: string; message?: string };
      if (body.message) return body.message;
      if (body.error) return body.error;
    } catch { /* not OPBridge's JSON: show the text as it is */ }
  }
  return text;
};
const is404 = (e: unknown) => (e as { httpStatus?: number } | null)?.httpStatus === 404;

/** Load the track list. A 404 means this OPBridge has no test-track routes. */
export async function refreshTracks(): Promise<void> {
  const c = primaryHttpClient();
  if (!c) return;
  try {
    const r = await c.testTracks();
    const list = Array.isArray(r?.tracks) ? r.tracks : [];
    setTracks(list);
    setTracksLoaded(true);
    setUnavailable(null);
    // A beat map or peaks asked for while the track was still analysing were cached as missing; ask again now.
    for (const t of list) {
      if (t.analysed && !t.analysing && (beatmaps()[t.id] === null || peaksById()[t.id] === null)) void loadTrack(t.id, true);
    }
  } catch (e) {
    setTracks([]);
    setUnavailable(is404(e) ? `${primaryBridge()?.name ?? 'OPBridge'} cannot play test tracks yet.` : message(e));
  }
}

/** Load (once) only the beat map of track `id`: its tempo, for the timeline pickers (2026-11-03), without the peaks a
 *  list of every timeline does not draw. One request per track at a time. */
const beatmapAsked = new Set<string>();
export async function loadBeatmap(id: string): Promise<void> {
  const c = primaryHttpClient();
  if (!c || id in beatmaps() || beatmapAsked.has(id)) return;
  beatmapAsked.add(id);
  let m: TestTrackBeatmap | null = null;
  try { m = await c.testTrackBeatmap(id); }
  catch (e) { if (!is404(e)) setLastError(message(e)); }
  finally { beatmapAsked.delete(id); }
  if (!(id in beatmaps())) setBeatmaps((o) => ({ ...o, [id]: m }));
}

/** Load (once) the beat map and peaks of track `id`. `null` in the cache = asked and not there (not analysed). */
export async function loadTrack(id: string, force = false): Promise<void> {
  const c = primaryHttpClient();
  if (!c) return;
  // Only the REQUEST is guarded: setting the signal runs everything that draws the song, and a throw there is a bug
  // to report, not a failed fetch (2026-11-02: one was caught here, swallowed, and the row said "Loading" for ever).
  if (force || !(id in beatmaps())) {
    let m: TestTrackBeatmap | null = null;
    try { m = await c.testTrackBeatmap(id); }
    catch (e) { if (!is404(e)) setLastError(message(e)); }
    setBeatmaps((o) => ({ ...o, [id]: m }));
  }
  if (force || !(id in peaksById())) {
    let p: TestTrackPeaks | null = null;
    try { p = await c.testTrackPeaks(id, 20); }
    catch (e) { if (!is404(e)) setLastError(message(e)); }
    setPeaksById((o) => ({ ...o, [id]: p }));
  }
}

/** Save corrections or section marks: one undo step (the song history), shown at once, then the reply (the
 *  updated file) replaces the cached copy. A refusal re-reads OPBridge's copy so the screen never shows a lie. */
export async function patchBeatmap(id: string, patch: TestTrackBeatmapPatch): Promise<boolean> {
  const cur = beatmaps()[id];
  if (cur) {
    songHistory.checkpoint();
    setBeatmaps((o) => ({ ...o, [id]: {
      ...cur,
      ...(patch.sections ? { sections: patch.sections } : {}),
      ...(patch.tempoChoice ? { tempoChoice: patch.tempoChoice } : {}),
      ...(patch.bar1Sec !== undefined ? { bar1_sec: patch.bar1Sec } : {}),
    } }));
  }
  return sendPatch(id, patch);
}

async function sendPatch(id: string, patch: TestTrackBeatmapPatch): Promise<boolean> {
  const c = primaryHttpClient();
  if (!c) return false;
  try {
    const m = await c.patchTestTrackBeatmap(id, patch);
    setBeatmaps((o) => ({ ...o, [id]: m }));
    setLastError(null);
    return true;
  } catch (e) {
    const why = message(e);
    setLastError(why);
    pushToast('error', `OPBridge did not save the song change: ${why}`);
    void loadTrack(id, true);
    return false;
  }
}

/** The PCs a track can play on: every bridge whose node id is known, by its name. */
export function playablePcs(): { nodeId: string; name: string }[] {
  return bridges().filter((b) => !!b.nodeId).map((b) => ({ nodeId: b.nodeId!, name: b.name }));
}

export async function outputsOf(pc: string): Promise<AudioOutput[]> {
  const c = primaryHttpClient();
  if (!c || !pc) return [];
  try { return (await c.audioOutputs(pc)).outputs ?? []; } catch (e) { setLastError(message(e)); return []; }
}

let poll: ReturnType<typeof setInterval> | null = null;
function startPoll() {
  if (poll) return;
  const tick = async () => {
    const c = primaryHttpClient();
    if (!c) return;
    try {
      const s = await c.testTrackStatus();
      setStatus(s);
      if (!s.playing) stopPoll();
    } catch { /* a missed poll is not an error worth showing */ }
  };
  void tick();
  poll = setInterval(tick, 500);
}
function stopPoll() { if (poll) { clearInterval(poll); poll = null; } }

export async function play(req: { id: string; pc: string; output: string; fromBar?: number; loop?: { fromBar: number; toBar: number } }): Promise<boolean> {
  const c = primaryHttpClient();
  if (!c) return false;
  try { await c.testTrackPlay(req); setLastError(null); startPoll(); return true; }
  catch (e) { setLastError(message(e)); return false; }
}

export async function stop(): Promise<void> {
  const c = primaryHttpClient();
  if (!c) return;
  try { await c.testTrackStop(); setLastError(null); } catch (e) { setLastError(message(e)); }
  stopPoll();
  setStatus((s) => (s ? { ...s, playing: false } : s));
}
